import { Box, Flex, HStack, Spacer, Text } from "@chakra-ui/react";
import { useRef } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { customCurrencyFormat } from "../../utils";

const MetricGraph = ({ metricGraphData }) => {
  const containerRef = useRef(null);

  // Prepare chart data
  const chartData =
    metricGraphData?.months?.map((month, index) => ({
      name: month,
      income: metricGraphData?.income[index] || 0,
      expenses: metricGraphData?.expense[index] || 0,
      profit: metricGraphData?.profit[index] || 0,
    })) || [];

  // Custom Tooltip
  const CustomTooltip = ({ active, payload, coordinate }) => {
    if (
      active &&
      payload &&
      payload.length &&
      coordinate &&
      containerRef.current
    ) {
      const income = payload.find((p) => p.dataKey === "income")?.value || 0;
      const expenses =
        payload.find((p) => p.dataKey === "expenses")?.value || 0;
      const profit = payload.find((p) => p.dataKey === "profit")?.value || 0;

      const containerRect = containerRef.current.getBoundingClientRect();
      const tooltipWidth = 180;
      const tooltipHeight = 120;
      const tooltipMargin = 10;
      const tooltipArrowSize = 8;
      const offset = 10;

      let left = coordinate.x;
      let top = coordinate.y - tooltipHeight - offset;
      let placeAbove = true;

      const minLeft = tooltipWidth / 2 + tooltipMargin;
      const maxLeft = containerRect.width - tooltipWidth / 2 - tooltipMargin;
      if (left < minLeft) left = minLeft;
      else if (left > maxLeft) left = maxLeft;

      const maxTop = containerRect.height - tooltipHeight - tooltipMargin;
      if (top > maxTop) top = maxTop;

      const tooltipLeftEdge = left - tooltipWidth / 2;
      let arrowLeftPx = coordinate.x - tooltipLeftEdge;

      const arrowMargin = 12;
      arrowLeftPx = Math.min(
        Math.max(arrowLeftPx, arrowMargin),
        tooltipWidth - arrowMargin
      );

      return (
        <Box
          position="absolute"
          top={`${top}px`}
          left={left}
          transform="translateX(-50%)"
          zIndex={1000}
          pointerEvents="none"
        >
          <Box
            bg="white"
            borderRadius="10px"
            boxShadow="lg"
            borderWidth="1px"
            borderColor="gray.300"
            p="10px"
            minW={`${tooltipWidth}px`}
            minH={`${tooltipHeight}px`}
            position="relative"
            _after={{
              content: '""',
              position: "absolute",
              borderWidth: `${tooltipArrowSize}px`,
              borderStyle: "solid",
              ...(placeAbove
                ? {
                    bottom: `-${tooltipArrowSize * 2}px`,
                    left: arrowLeftPx + 3,
                    borderColor: "white transparent transparent transparent",
                    transform: "translateX(0)",
                  }
                : {
                    top: `-${tooltipArrowSize * 2}px`,
                    left: arrowLeftPx,
                    borderColor: "transparent transparent white transparent",
                    transform: "translateX(0)",
                  }),
            }}
          >
            <HStack p="4px 0">
              <Box w="20px" h="8px" bg="#458CFF" borderRadius="5px" />
              <Text>Income:</Text>
              <Spacer />
              <Text>{customCurrencyFormat(income)}</Text>
            </HStack>
            <HStack p="4px 0" borderBottom="1px solid" borderColor="gray.200">
              <Box w="20px" h="8px" bg="#FF5A5C" borderRadius="5px" />
              <Text>Expenses:</Text>
              <Spacer />
              <Text>{customCurrencyFormat(expenses)}</Text>
            </HStack>
            <HStack p="4px 0">
              <Box w="20px" h="4px" bg="#00D26C" borderRadius="5px" />
              <Text>Profit:</Text>
              <Spacer />
              <Text color={profit < 0 ? "#FF5A5C" : "#00D26C"}>
                {customCurrencyFormat(profit)}
              </Text>
            </HStack>
          </Box>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box
      ref={containerRef}
      width="100%"
      height="100%"
      position="relative"
      sx={{
        "& svg:focus, g:focus, path:focus": {
          outline: "none !important",
        },
      }}
    >
      {/* Title + Legend */}
      <Flex justify="space-between" align="center" mb={4} wrap="wrap">
        <Text fontSize="18px" fontWeight={500}>
          12-Month Income, Expenses and Profit
        </Text>
        <HStack spacing={3} ml="auto">
          <HStack>
            <Box w="20px" h="8px" bg="#458CFF" borderRadius="5px" />
            <Text fontSize="14px">Income</Text>
          </HStack>
          <HStack>
            <Box w="20px" h="8px" bg="#FF5A5C" borderRadius="5px" />
            <Text fontSize="14px">Expenses</Text>
          </HStack>
          <HStack>
            <Box w="20px" h="4px" bg="#00D26C" borderRadius="5px" />
            <Text fontSize="14px">Profit</Text>
          </HStack>
        </HStack>
      </Flex>

      <ResponsiveContainer
        width="100%"
        height={250}
        style={{ outline: "none" }}
        tabIndex={-1}
      >
        <ComposedChart
          data={chartData}
          margin={{ top: 20, right: 20, left: -10, bottom: 20 }}
        >
          <Tooltip content={<CustomTooltip />} cursor={false} />
          <CartesianGrid stroke="#CCCCCC" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 14, fill: "#666" }}
            axisLine={{ stroke: "#CCCCCC" }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 14, fill: "#666" }}
            axisLine={{ stroke: "#CCCCCC" }}
            tickLine={false}
            tickFormatter={(value) => {
              if (value === 0) return "0";
              if (Math.abs(value) >= 1000)
                return `${(value / 1000).toFixed(0)}K`;
              return `$${value}`;
            }}
          />
          <Bar
            dataKey="income"
            barSize={30}
            fill="#458CFF"
            radius={[10, 10, 0, 0]}
          />
          <Bar
            dataKey="expenses"
            barSize={30}
            fill="#FF5A5C"
            radius={[10, 10, 0, 0]}
          />
          <Line
            type="monotone"
            dataKey="profit"
            stroke="#00D26C"
            strokeWidth={4}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default MetricGraph;
